import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
import './Van3D.css';

// Smoke particles component for wheel dust effect
function SmokeParticles({ isAnimating }) {
  const particlesRef = useRef();
  const particleCount = 100; // Increased for more visible effect

  // Create particles
  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const lifetimes = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      // Start behind wheels (X axis - left side behind wheels)
      positions[i * 3] = -2 - Math.random() * 1; // X: behind wheels (negative X)
      positions[i * 3 + 1] = -0.5 + Math.random() * 0.3; // Y: near ground
      positions[i * 3 + 2] = (Math.random() - 0.5) * 1.5; // Z: spread along wheel width

      velocities[i * 3] = -Math.random() * 0.05; // X: move backward (left)
      velocities[i * 3 + 1] = Math.random() * 0.02; // Y: rise up
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.02; // Z: slight spread

      lifetimes[i] = Math.random();
    }

    return { positions, velocities, lifetimes };
  }, []);

  useFrame((state, delta) => {
    if (!isAnimating || !particlesRef.current) return;

    const positions = particlesRef.current.geometry.attributes.position.array;
    const { velocities, lifetimes } = particles;

    for (let i = 0; i < particleCount; i++) {
      // Update position
      positions[i * 3] += velocities[i * 3];
      positions[i * 3 + 1] += velocities[i * 3 + 1];
      positions[i * 3 + 2] += velocities[i * 3 + 2];

      // Update lifetime
      lifetimes[i] += delta * 0.5;

      // Reset particle when it dies or goes too far
      if (lifetimes[i] > 1 || positions[i * 3] < -5) {
        positions[i * 3] = -2 - Math.random() * 0.5; // X: reset behind wheels
        positions[i * 3 + 1] = -0.5 + Math.random() * 0.3; // Y: near ground
        positions[i * 3 + 2] = (Math.random() - 0.5) * 1.5; // Z: spread
        lifetimes[i] = 0;
      }
    }

    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles.positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.25}
        color="#999999"
        transparent
        opacity={0.6}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

function VanModel({ isAnimating, canvasVelocity }) {
  const vanRef = useRef();
  const wheelsRef = useRef([]);
  const shadowRef = useRef();

  // Load the GLB model
  const { scene } = useGLTF('/assets/van.glb');

  // Find wheels in the model
  useEffect(() => {
    if (scene) {
      const wheels = [];
      const allMeshes = [];

      // Debug: log all mesh names and objects in the model
      console.log('🚐 Van2 Model Structure (Detailed):');
      scene.traverse((child) => {
        if (child.isMesh) {
          allMeshes.push(child);
          console.log('  📦 Mesh:', child.name, '| Type:', child.type, '| Geometry:', child.geometry?.type);

          // Make windows transparent to show desktop background
          const nameLower = child.name.toLowerCase();
          if (nameLower.includes('glass') ||
              nameLower.includes('window') ||
              nameLower.includes('windshield') ||
              nameLower.includes('windscreen')) {
            if (child.material) {
              child.material.transparent = true;
              child.material.opacity = 0.3; // Semi-transparent
              child.material.needsUpdate = true;
              console.log('  🪟 Window made transparent:', child.name);
            }
          }
        }
        if (child.isObject3D && !child.isMesh) {
          console.log('  📁 Object3D:', child.name, '| Type:', child.type);
        }

        // Expanded search - look for wheel objects with various naming patterns
        const nameLower = child.name.toLowerCase();

        // Only match tire/tyre specifically (not wheel, rim, etc. which might match steering wheel)
        if (child.isMesh && (
          nameLower.includes('tire') ||
          nameLower.includes('tyre')
        )) {
          wheels.push(child);

          console.log('  🎯 Tire found:', child.name, '| Initial rotation:',
            child.rotation.x.toFixed(2), child.rotation.y.toFixed(2), child.rotation.z.toFixed(2));

          // Don't modify initial rotation - keep wheels in their original position
          // We'll use rotateOnAxis for proper local rotation during animation
        }
      });

      wheelsRef.current = wheels;

      console.log('📊 Summary:');
      console.log('  Total meshes:', allMeshes.length);
      console.log('  Potential wheels:', wheels.length);

      // Debug: log wheel names
      if (wheels.length > 0) {
        console.log('✅ Found wheels:', wheels.map(w => w.name));
      } else {
        console.log('❌ No wheels found - listing all meshes:');
        allMeshes.forEach(m => console.log('    -', m.name || '(unnamed)'));
      }
    }
  }, [scene]);

  useFrame((state, delta) => {
    if (isAnimating && vanRef.current) {
      // Simulate driving motion with subtle bounce and tilt effects
      const time = state.clock.getElapsedTime();

      // Wheel rotation speed based on canvas velocity (0 to 1)
      const wheelSpeed = canvasVelocity * 5; // Reduced speed for more realistic rotation

      // Reduced vertical bounce (subtle suspension) - scales with velocity
      vanRef.current.position.y = Math.sin(time * 25) * 0.02 * (0.5 + canvasVelocity * 0.5);

      // Reduced side-to-side rock
      vanRef.current.rotation.z = Math.sin(time * 20) * 0.008 * (0.5 + canvasVelocity * 0.5);

      // Reduced front-to-back tilt
      vanRef.current.rotation.x = Math.sin(time * 18) * 0.006 * (0.5 + canvasVelocity * 0.5);

      // Rotate wheels if found - speed based on canvas movement
      wheelsRef.current.forEach((wheel) => {
        // Use rotateOnAxis for local rotation (not world rotation)
        // X axis for the wheel's rolling motion (negative for clockwise)
        const axis = new THREE.Vector3(1, 0, 0); // X axis in local space
        wheel.rotateOnAxis(axis, -delta * wheelSpeed); // Speed based on canvas velocity
      });

      // Update shadow opacity based on velocity (darker when moving faster)
      if (shadowRef.current) {
        shadowRef.current.material.opacity = 0.4 + (canvasVelocity * 0.3);
      }
    } else if (vanRef.current) {
      // Reset to original position when not animating
      vanRef.current.position.y = 0;
      vanRef.current.rotation.z = 0;
      vanRef.current.rotation.x = 0;
    }
  });

  return (
    <>
      {/* Shadow plane under the van */}
      <mesh ref={shadowRef} position={[0, -0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[2.5, 1.5]} />
        <meshBasicMaterial
          color="#000000"
          transparent
          opacity={0.4}
          depthWrite={false}
        />
      </mesh>

      {/* Van model */}
      <primitive
        ref={vanRef}
        object={scene}
        scale={[1.0, 1.0, 1.0]} // Reduced scale to fit in canvas
        position={[0, 0, 0]} // Van stays centered in canvas
        rotation={[0, 0, Math.PI / 2]} // Rotate 90 degrees on Z axis
      />
    </>
  );
}

const Van3D = ({ onClose }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [canvasVelocity, setCanvasVelocity] = useState(0);
  const containerRef = useRef(null);

  // Calculate canvas positions
  const desktopWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const canvasWidth = 600;
  const centerLeft = (desktopWidth - canvasWidth) / 2; // Center position
  const startLeft = -canvasWidth; // Start off-screen to the left

  const lastPositionRef = useRef(startLeft);
  const [position, setPosition] = useState({ left: startLeft, bottom: 180 });

  // Van stays at center of canvas (position 0 in 3D space)
  // Canvas itself will move from left to center
  const targetXInCanvas = 0;

  useEffect(() => {
    // Start animation when component mounts
    setIsAnimating(true);

    // Animate canvas from left to center
    const animationDuration = 3000; // 3 seconds
    const startTime = Date.now();
    let lastTime = startTime;

    const animateCanvas = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);

      // Ease-out function for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const currentLeft = startLeft + (centerLeft - startLeft) * easeProgress;

      // Calculate velocity (normalized 0 to 1)
      const deltaTime = now - lastTime;
      const deltaPos = currentLeft - lastPositionRef.current;
      const velocity = Math.abs(deltaPos) / (canvasWidth * 0.016); // Normalize to 0-1 range
      setCanvasVelocity(Math.min(velocity, 1));

      lastPositionRef.current = currentLeft;
      lastTime = now;

      setPosition(prev => ({ ...prev, left: currentLeft }));

      if (progress < 1) {
        requestAnimationFrame(animateCanvas);
      } else {
        setIsAnimating(false);
        setAnimationComplete(true);
        setCanvasVelocity(0);
      }
    };

    requestAnimationFrame(animateCanvas);
  }, [startLeft, centerLeft, canvasWidth]);

  const handleMouseDown = (e) => {
    if (!animationComplete) return; // Only allow dragging after animation
    if (e.target.closest('.van3d-close-btn')) return; // Don't drag when clicking close button

    e.preventDefault();
    setIsDragging(true);
    const containerHeight = 500; // Increased container height
    setDragStart({
      x: e.clientX - position.left,
      y: e.clientY - (window.innerHeight - position.bottom - containerHeight)
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    e.preventDefault();
    const containerHeight = 500; // Increased container height
    const newLeft = e.clientX - dragStart.x;
    const newBottom = window.innerHeight - e.clientY - dragStart.y - containerHeight;

    // Constrain to viewport bounds
    const maxLeft = window.innerWidth - canvasWidth;
    const maxBottom = window.innerHeight - containerHeight - 40; // taskbar height

    setPosition({
      left: Math.max(0, Math.min(newLeft, maxLeft)),
      bottom: Math.max(0, Math.min(newBottom, maxBottom))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="van3d-container"
      style={{
        left: `${position.left}px`,
        bottom: `${position.bottom}px`,
        pointerEvents: 'none' // Container itself is not interactive
      }}
    >
      <Canvas className="van3d-canvas">
        <PerspectiveCamera makeDefault position={[0, 0, 8]} fov={50} />

        {/* Very Bright Lighting for maximum visibility */}
        <ambientLight intensity={2.0} />
        <directionalLight position={[5, 5, 5]} intensity={2.5} />
        <directionalLight position={[-5, 5, -5]} intensity={2.0} />
        <directionalLight position={[0, -3, 5]} intensity={1.5} /> {/* Bottom light */}
        <pointLight position={[3, 3, 3]} intensity={1.5} />
        <pointLight position={[-3, 3, -3]} intensity={1.5} />
        <hemisphereLight args={['#ffffff', '#888888', 1.5]} /> {/* Sky and ground light */}
        <spotLight position={[0, 5, 0]} intensity={1.0} angle={0.6} penumbra={0.5} />

        {/* Van Model - stays centered while canvas moves */}
        <VanModel isAnimating={isAnimating} canvasVelocity={canvasVelocity} />

        {/* Smoke/dust particles behind wheels */}
        <SmokeParticles isAnimating={isAnimating} />
      </Canvas>

      {/* Center drag handle - only show after animation */}
      {animationComplete && (
        <div
          className="van3d-center-handle"
          onMouseDown={handleMouseDown}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          title="Drag to move"
        />
      )}

      {/* Close button on front right wheel - only show after animation */}
      {animationComplete && (
        <button className="van3d-close-btn" onClick={onClose} title="Close Van">
          ✕
        </button>
      )}
    </div>
  );
};

export default Van3D;
